/**
 * Subscriptions feature types
 *
 * Re-exports generated types and defines feature-specific extensions.
 */

// Re-export generated types for convenience
export type {
  ActiveBundleResponse,
  ActiveBundlesResponse,
  BundlePriceResponse,
  EntitlementListResponse,
  EntitlementResponse,
  SubscriptionStatusResponse,
} from '@store/generated/subscriptions';

// Flow type based on subscription status
export type SubscriptionFlowType = 'checkout' | 'modify';

// Billing interval type
export type BillingInterval = 'month' | 'year';

// Bundle type
export type BundleType = 'tier' | 'add_on';

/**
 * Convert a bundle name to a URL-friendly slug
 * "EHR Essentials" -> "ehr-essentials"
 * "JotAudit" -> "jotaudit"
 */
export function slugifyBundleName(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, '') // Remove ampersands
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
    .replace(/^-|-$/g, ''); // Trim leading/trailing hyphens
}

// Feature type with inclusion flag
export interface Feature {
  name: string;
  included: boolean;
}

// Capability grant info from the bundle catalog
export interface CapabilityGrant {
  capability_name: string;
  metering_type: string; // 'none' | 'fully_metered' | 'hybrid'
  quota: number | null; // null = unlimited
  usage_period?: string | null;
}

// Bundle with status flags and dual pricing for UI
export interface BundleWithStatus {
  bundle_id: string;
  name: string;
  description?: string;
  bundle_type: BundleType;
  compatible_addon_ids?: string[] | null; // For tier bundles
  monthly_unit_amount: number;
  monthly_price_id: string;
  yearly_unit_amount: number;
  yearly_price_id: string;
  currency: string;
  stripe_product_id: string;
  marketing_features?: Feature[];
  learn_more_url?: string;
  grants?: CapabilityGrant[];
  intro_offer?: { percent_off?: number | null; amount_off?: number | null } | null;
  isSelected: boolean;
}

// ============================================================================
// Add-on Bundles Page Config Types (Statsig Dynamic Config)
// ============================================================================

/**
 * Individual feature within a bundle section
 */
export interface BundleFeature {
  title: string;
  description: string;
  bullets?: string[];
}

/**
 * Content section within a bundle tab (e.g., "Lab orders", "Forms & assessments")
 */
export interface BundleSection {
  title: string;
  description?: string;
  features?: BundleFeature[];
}

/**
 * FAQ item for a bundle tab
 */
export interface BundleFaq {
  question: string;
  answer: string;
}

/**
 * Hero section at the top of each bundle tab
 */
export interface BundleHero {
  title: string;
  subtitle: string;
  description: string;
  pricing?: string;
}

/**
 * Individual tab configuration for the Add-on Bundles page
 */
export interface AddOnBundleTab {
  id?: string; // Optional - derived from bundleName if not provided
  label: string;
  bundleName: string; // Required - links to bundle name in catalog for pricing and tab ID
  hero: BundleHero;
  sections: BundleSection[];
  faq: BundleFaq[];
  showTalkToSales?: boolean;
  calendlyUrl?: string;
}

/**
 * Complete configuration for the Add-on Bundles page
 * Controlled via Statsig Dynamic Config: addon_bundles_config
 */
export interface AddOnBundlesConfig {
  pageTitle: string;
  pageIntro: string;
  tabs: AddOnBundleTab[];
}

/**
 * Default configuration for the Add-on Bundles page
 * Used as fallback when Statsig config is not available
 */
export const DEFAULT_ADDON_BUNDLES_CONFIG: AddOnBundlesConfig = {
  pageTitle: 'Add-on Bundles',
  pageIntro:
    'Go beyond note-taking. Add EHR tools, billing support, and security features to support your full clinical workflow.',
  tabs: [
    {
      label: 'EHR Essentials',
      bundleName: 'EHR Essentials',
      hero: {
        title: 'EHR Essentials',
        subtitle: 'Patient paperwork, documentation, and notes— done your way',
        description:
          "EHR Essentials gives you the tools to handle labs, forms, documents, and notes, all in one place. It's designed to feel simple, save you time, and adapt to how you practice—whether you're solo or part of a group.",
        pricing: 'Priced per seat. Available for both solo clinicians and group practices.',
      },
      sections: [
        {
          title: "What's included",
          features: [
            {
              title: 'Lab orders',
              description: 'Create complete lab requisition forms without breaking your flow.',
              bullets: [
                'Pre-filled details: Patient demographics, provider and practice information, ICD-10 codes, and common instructions are filled in automatically.',
                'Built for repeat use: Save commonly ordered labs and reuse them across patients.',
                'Flexible delivery: Download or print a clean PDF, or securely email the form directly to the patient.',
              ],
            },
            {
              title: 'Forms & assessments',
              description:
                'Send screeners and intake forms with a single click. No portals. No logins. No extra steps for your patients.',
              bullets: [
                'Common assessments like PHQ-9, GAD-7, and other standard screeners',
                'Intake forms for new or returning patients',
                "Start from JotPsych's built-in library or create your own",
                'Easy delivery via secure text message or email',
                "Automatic tracking: Results are saved to the patient's profile",
              ],
            },
            {
              title: 'Clinical document generator',
              description:
                'Create clear, professional clinical documents in seconds, using your session notes as context.',
              bullets: [
                'Emotional support animal letters',
                'Work or academic accommodation letters',
                'FMLA and disability related letters',
                'Discharge summaries',
                'Medical necessity letters',
                'Referrals to PCPs, prescribers, or specialists',
              ],
            },
          ],
        },
      ],
      faq: [
        {
          question: 'Is usage really unlimited?',
          answer:
            'Yes. There are no caps on lab orders, documents, forms, AI edits, or templates for seats with EHR Essentials.',
        },
        {
          question: 'How much setup does this require?',
          answer:
            'Very little. You can start using labs, forms, and documents right away, and customize templates over time as you learn what works best for you.',
        },
        {
          question: 'Does this replace my EHR?',
          answer:
            'It can, but it does not have to. You can use JotPsych as your primary system or alongside your current EHR.',
        },
        {
          question: 'Is this secure and HIPAA-compliant?',
          answer:
            "Yes. All labs, forms, documents, and notes are handled securely within JotPsych's HIPAA-compliant platform.",
        },
      ],
    },
    {
      label: 'JotAudit',
      bundleName: 'JotAudit',
      hero: {
        title: 'JotAudit',
        subtitle: 'Catch billing issues before claims go out',
        description:
          'JotAudit looks at your documentation through a billing and audit lens and points out what may be missing or unclear. It offers you practical, note-level feedback you can act on.',
      },
      sections: [
        {
          title: 'What JotAudit does',
          features: [
            {
              title: 'Documentation review',
              description: 'Reviews notes against a library of billing and compliance rules',
              bullets: [
                'Flags gaps that can lead to denials, downcoding, or audit questions',
                "Explains what's missing and why it matters",
                'Offers AI-assisted suggestions to fix or clarify notes',
              ],
            },
            {
              title: 'Custom rules',
              description:
                'Add your own rules—reflecting clinic policies, payer requirements, or training needs—so the same guidance applies across your team.',
              bullets: [
                'Every practice bills a little differently',
                'Payer contracts come with specific expectations',
                'New clinicians benefit from shared guardrails',
              ],
            },
          ],
        },
        {
          title: 'Why this matters',
          description:
            'With JotAudit, you can keep everyone aligned while finishing documentation confidently and on time.',
        },
      ],
      faq: [
        {
          question: 'Do I need to run JotAudit on every note?',
          answer:
            'No. Some clinicians use it selectively, while others make it part of their standard workflow. You decide where it adds the most value.',
        },
        {
          question: 'Can rules be shared across a practice?',
          answer: 'Yes. Practice-level rules help keep expectations consistent across clinicians.',
        },
        {
          question: 'Does JotAudit guarantee payment?',
          answer:
            'No tool can guarantee reimbursement. JotAudit is designed to reduce preventable issues and support more consistent documentation.',
        },
        {
          question: 'What if I bill differently than the defaults?',
          answer:
            'That is expected. Custom rules let you adapt JotAudit to match your billing approach.',
        },
      ],
    },
  ],
};
